import { FC } from 'react';

import { PlaceholderBar } from '../../components/loading-skeleton/index';

type PoolPlansPlaceholderProps = {
  rows?: number;
};

// Scrollable-part data columns of PoolPlansCommonTable — one per PoolsPlansTableColumnTitle
// member (diluted shares, available pool, pool granted, total grant vested, shares vested,
// exercisable, exercised, cash settled, returned to pool, expired).
const SCROLLABLE_COLUMNS = 10;

// Table-only skeleton — shown in place of the pools/plans table during pagination or
// vest-date reloads, keeping the tabs + header (add/export/filter) in place.
// Mirrors PoolPlansCommonTable: sticky checkbox + Pool name part, then the scrollable
// part with the PoolsPlansTableColumnTitle columns + actions (pools-table/pool-table.tsx).
export const PoolPlansTablePlaceholder: FC<PoolPlansPlaceholderProps> = ({ rows = 6 }) => (
  <div className="flex w-full flex-1 animate-pulse">
    <div className="z-10 h-full flex-shrink-0">
      <table className="h-fit">
        <thead>
          <tr className="flex border-y-[1px] border-[#F2F2F2] bg-gray-50">
            <td className="relative">
              <div className="flex h-full w-[48px] items-center justify-center">
                <PlaceholderBar className="size-4 rounded bg-gray-100" />
              </div>
            </td>
            <td className="px-4 py-3">
              <div className="flex min-w-[210px] max-w-[320px] items-center">
                <PlaceholderBar className="h-4 w-12 rounded bg-gray-100" />
              </div>
            </td>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, index) => (
            <tr className="flex h-[72px] w-full bg-white" key={index}>
              <td className="h-[72px] w-[48px] border-b border-b-gray-100">
                <div className="flex h-full items-center justify-center">
                  <PlaceholderBar className="size-4 rounded bg-gray-100" />
                </div>
              </td>
              <td className="flex h-[72px] flex-1 items-center border-b border-b-gray-100 px-4">
                <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="h-full flex-grow overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-y-[1px] border-[#F2F2F2] bg-gray-50">
            {Array.from({ length: SCROLLABLE_COLUMNS }).map((_, index) => (
              <td className="px-4 py-3" key={index}>
                <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
              </td>
            ))}
            <td className="relative">
              <div className="flex h-full w-full items-center justify-center px-6"></div>
            </td>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, index) => (
            <tr className="h-[72px] border-b border-b-gray-100 bg-white" key={index}>
              {Array.from({ length: SCROLLABLE_COLUMNS }).map((_cell, cellIndex) => (
                <td className="p-4" key={cellIndex}>
                  <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
                </td>
              ))}
              <td className="relative px-6">
                <PlaceholderBar className="size-5 rounded bg-gray-100" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const PoolPlansPlaceholder: FC<PoolPlansPlaceholderProps> = ({ rows = 6 }) => (
  <div className="flex h-fit min-h-full w-full animate-pulse flex-col gap-4 overflow-x-auto rounded-md lg:p-4 lg:shadow-sm">
    <div className="flex h-[36px] w-full gap-4 border-b border-gray-200">
      <PlaceholderBar className="h-5 w-20 rounded bg-gray-100" />
      <PlaceholderBar className="h-5 w-24 rounded bg-gray-100" />
    </div>
    <div className="flex h-fit w-full flex-1 flex-col gap-4 rounded-md py-4 shadow-sm">
      <div className="flex w-full items-center justify-between gap-4 px-4">
        <PlaceholderBar className="h-9 w-44 rounded border border-gray-200 bg-gray-100" />
        <div className="flex gap-2">
          <PlaceholderBar className="h-9 w-28 rounded bg-gray-100" />
          <PlaceholderBar className="h-9 w-28 rounded bg-gray-100" />
        </div>
      </div>
      <PoolPlansTablePlaceholder rows={rows} />
    </div>
  </div>
);
